import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { ChapterResponse, assetsChaptersBulkAddUpdateCreate } from '@goldcast/api/content';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import {
  computeChapterAdjustments,
  isChapterAffectingAction
} from '../clipUpdateDataUtilsWithSplits/chapterUpdateUtils';
import { UpdateClipAction } from '../clipUpdateDataUtilsWithSplits/types';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { core } from '@/stores/core';
import { addPendingUpdate, PendingEditType } from '@/stores/pendingEditsStore';
import { injectCurrentHistoryWithChapters } from '@/stores/clipHistory';
import { showErrorToast } from '@/libs/toast/toast';

interface ChapterAdjustmentEvent {
  actionType: UpdateClipAction['actionType'];
  updateClipPayload: UpdateClipAction;
  oldClip: Clip;
  newClip: Clip;
}

const areChaptersEquivalent = (current: ChapterResponse[], target: ChapterResponse[]) => {
  if (current.length !== target.length) {
    return false;
  }
  const sortedCurrent = [...current].sort((a, b) => a.start_time - b.start_time);
  const sortedTarget = [...target].sort((a, b) => a.start_time - b.start_time);
  for (let index = 0; index < sortedCurrent.length; index++) {
    const left = sortedCurrent[index];
    const right = sortedTarget[index];
    if (left.id !== right.id || left.start_time !== right.start_time || left.title !== right.title) {
      return false;
    }
  }
  return true;
};

export const useChapterAutoAdjust = (): void => {
  const transcriptStore = useTranscriptContext();
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const broadcastId = useMemo(() => coreStore.content?.id, [coreStore.content]);

  const chaptersRef = useRef(chapters);
  const broadcastIdRef = useRef(broadcastId);
  const isProcessingRef = useRef(false);
  const eventQueueRef = useRef<ChapterAdjustmentEvent[]>([]);

  useEffect(() => {
    chaptersRef.current = chapters;
  }, [chapters]);

  useEffect(() => {
    broadcastIdRef.current = broadcastId;
  }, [broadcastId]);

  const processEvent = useCallback(async (event: ChapterAdjustmentEvent): Promise<void> => {
    const currentChapters = chaptersRef.current;
    const currentBroadcastId = broadcastIdRef.current;

    if (!currentChapters || !currentBroadcastId) {
      return;
    }
    if (!isChapterAffectingAction(event.actionType)) {
      return;
    }

    const result = computeChapterAdjustments({
      actionType: event.actionType,
      updateClipPayload: event.updateClipPayload,
      oldClip: event.oldClip,
      newClip: event.newClip,
      chapters: currentChapters
    });

    if (!result) {
      return;
    }

    if (areChaptersEquivalent(currentChapters, result)) {
      return;
    }

    EventBus.dispatch(CustomEvents.UpdateChapters, {
      chapterData: result,
      chaptersLastUpdatedAt: new Date().toISOString()
    });
    // TODO This flow needs to be removed and directly moved into updateClipData functions.
    // TODO This is put here just to inject chapter details in case we have a autoAdjusted Changes.
    injectCurrentHistoryWithChapters(event.newClip.id, result);
    const finalChapters = await persistChapterChanges(result, currentBroadcastId, event.newClip.id);
    chaptersRef.current = finalChapters;
  }, []);

  const handleChapterAdjustment = useCallback(
    async (event: ChapterAdjustmentEvent) => {
      eventQueueRef.current.push(event);

      if (isProcessingRef.current) {
        return;
      }

      isProcessingRef.current = true;
      try {
        while (eventQueueRef.current.length > 0) {
          const next = eventQueueRef.current.shift()!;
          await processEvent(next);
        }
      } finally {
        isProcessingRef.current = false;
      }
    },
    [processEvent]
  );

  useEffect(() => {
    const listener = EventBus.on(CustomEvents.ChapterAdjustmentNeeded, handleChapterAdjustment);
    return () => {
      EventBus.off(CustomEvents.ChapterAdjustmentNeeded, listener);
    };
  }, [handleChapterAdjustment]);
};

async function persistChapterChanges(
  result: ChapterResponse[],
  broadcastId: string,
  clipId?: string
): Promise<ChapterResponse[]> {
  try {
    // First, bulk update existing chapters
    const bulkUpdatePromise = assetsChaptersBulkAddUpdateCreate({
      body: result,
      queryParams: { content_id: broadcastId }
    });
    if (clipId) {
      addPendingUpdate({
        clipId,
        updatePromise: bulkUpdatePromise,
        version: Date.now(),
        editType: PendingEditType.chapter
      });
    }
    await bulkUpdatePromise;
    return result;
  } catch (e) {
    window.logger?.error('Failed to persist chapter adjustments', e);
    showErrorToast('Failed to adjust chapters. Please try again.');
    return result;
  }
}
